import React, { useState, useEffect, useCallback } from 'react';
import { hotelBookingApi } from '../api/client';

// The internal booking screen (task 1.24). This is the product ETG certifies:
// a back-office screen behind the login, not the customer website.
//
// The steps follow the booking chain of the backend: rates from the hotel
// page, a price check (prebook) that creates our booking row, guest names,
// then "in behandeling" until the status step says confirmed or failed. The
// rules that matter are enforced by the backend, not here: a changed price is
// only booked after confirmation, and the 15-minute cut-off is counted from
// the start of the booking. This screen only has to show them honestly.

const POLL_MS = 5000;
const MAX_ROOMS = 9;

const RESIDENCIES = [
  ['be', 'België'],
  ['nl', 'Nederland'],
  ['fr', 'Frankrijk'],
  ['de', 'Duitsland'],
  ['lu', 'Luxemburg'],
  ['gb', 'Verenigd Koninkrijk'],
  ['ch', 'Zwitserland'],
  ['it', 'Italië'],
  ['es', 'Spanje'],
  ['us', 'Verenigde Staten'],
];

const MEALS = {
  nomeal: 'geen maaltijd',
  breakfast: 'ontbijt',
  'breakfast-buffet': 'ontbijtbuffet',
  'continental-breakfast': 'continentaal ontbijt',
  'half-board': 'halfpension',
  'full-board': 'volpension',
  'all-inclusive': 'all-in',
};

const TAXES = {
  city_tax: 'toeristenbelasting',
  occupancy_tax: 'verblijfsbelasting',
  service_fee: 'servicekosten',
  resort_fee: 'resortkosten',
  vat: 'btw',
};

const STATUS = {
  prebooked: ['prijs gecontroleerd', 'no'],
  pending: ['in behandeling', 'warning'],
  succeeded: ['bevestigd', 'yes'],
  failed: ['mislukt', 'price-type-dynamic'],
  cancelled: ['geannuleerd', 'no'],
};

const FAILURES = {
  soldout: 'uitverkocht',
  book_limit: 'boekingslimiet bij RateHawk bereikt',
  provider: 'fout bij de leverancier van het hotel',
  block: 'betaling geblokkeerd',
  charge: 'betaling mislukt',
  '3ds': 'betaling niet bevestigd',
  booking_timeout: 'geen uitkomst binnen 15 minuten',
  booking_form_expired: 'het tarief is verlopen, vraag opnieuw tarieven op',
  rate_not_found: 'het tarief bestaat niet meer, vraag opnieuw tarieven op',
  insufficient_b2b_balance: 'onvoldoende saldo op de RateHawk-rekening',
};

// A date field reports an empty value while a date is half typed, and
// toISOString throws on the invalid date that makes. Thrown during rendering,
// that emptied the whole screen (06/10/2026). So: no date in, no date out.
const addDays = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const nightsBetween = (from, to) =>
  Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000);

const inDays = (n) => addDays(new Date().toISOString().slice(0, 10), n);

const date = (iso) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('nl-BE') : '';

// RateHawk gives its deadlines in UTC. Shown in Belgian time, whatever the
// clock of the computer says, because that is the time we talk in.
const moment = (iso) =>
  iso
    ? new Date(iso).toLocaleString('nl-BE', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Brussels',
      })
    : '';

const money = (amount, currency) =>
  amount === null || amount === undefined
    ? ''
    : `${Number(amount).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency || ''}`.trim();

// Everything on this screen in euro. RateHawk charges in the contract
// currency (USD in the sandbox); the backend converts with RateHawk's own rate
// of the same answer. Older bookings without a rate keep their own currency.
const eur = (amountEur, amount, currency) =>
  amountEur !== null && amountEur !== undefined ? money(amountEur, 'EUR') : money(amount, currency);

const occupancyText = (rooms) =>
  rooms
    .map((r) => {
      const kids = r.children.length ? ` + ${r.children.length} kind (${r.children.join(', ')} j)` : '';
      return `${r.adults} volw.${kids}`;
    })
    .join(' / ');

const errorText = (error, fallback) => {
  const detail = error.response && error.response.data && error.response.data.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return fallback;
};

const failureText = (code) => (code ? FAILURES[code] || code : 'reden onbekend');

function StatusBadge({ status }) {
  const [label, kind] = STATUS[status] || [status, 'no'];
  return <span className={`badge ${kind}`}>{label}</span>;
}

function HotelInfo({ hotel }) {
  if (!hotel) return null;
  return (
    <div className="booking-panel">
      <h4>{hotel.name}</h4>
      <div className="detail-header">
        {hotel.address && <span>{hotel.address}</span>}
        {hotel.location_name && <span>{hotel.location_name}</span>}
        {hotel.check_in_time && <span>inchecken vanaf {hotel.check_in_time}</span>}
        {hotel.check_out_time && <span>uitchecken tot {hotel.check_out_time}</span>}
      </div>
      {hotel.policies.length > 0 || hotel.extra_info ? (
        <>
          <p className="booking-label">Voorwaarden van het hotel, ter plaatse te betalen</p>
          {hotel.policies.length > 0 && (
            <ul className="policy-list">
              {hotel.policies.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {hotel.extra_info && <p className="notes">{hotel.extra_info}</p>}
        </>
      ) : (
        <p className="empty">Het hotel gaf geen bijkomende voorwaarden op.</p>
      )}
    </div>
  );
}

function Cancellation({ rate }) {
  if (!rate.cancellation_tiers.length) {
    return <span className="empty">niet kosteloos te annuleren</span>;
  }
  return (
    <>
      {rate.free_cancellation_before && (
        <div>kosteloos tot {moment(rate.free_cancellation_before)}</div>
      )}
      {rate.cancellation_tiers
        .filter((t) => Number(t.cost) > 0)
        .map((t) => (
          <div key={`${t.from_date}-${t.until_date}`} className="small-line">
            {t.from_date ? `vanaf ${moment(t.from_date)}` : 'vanaf de boeking'}
            {t.until_date ? ` tot ${moment(t.until_date)}` : ''}: {money(t.displayed_cost, rate.displayed_currency)}
          </div>
        ))}
    </>
  );
}

function RatesTable({ rates, roomCount, onChoose, busy }) {
  if (!rates.length) {
    return <p className="empty">Geen tarieven voor deze vraag. Probeer een andere datum of bezetting.</p>;
  }
  return (
    <table className="insights-table">
      <thead>
        <tr>
          <th>Kamer</th>
          <th>Maaltijd</th>
          <th className="number">{roomCount > 1 ? `Prijs, ${roomCount} kamers samen` : 'Prijs'}</th>
          <th>Annuleren</th>
          <th>Taksen ter plaatse</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rates.map((r) => (
          <tr key={r.book_hash} className="no-pointer">
            <td>{r.room}</td>
            <td>{MEALS[r.meal] || r.meal}</td>
            <td className="number">
              {money(r.displayed_price, r.displayed_currency)}
            </td>
            <td>
              <Cancellation rate={r} />
            </td>
            <td>
              {r.taxes_at_property.length ? (
                r.taxes_at_property.map((t) => (
                  <div key={t.name} className="small-line">
                    {TAXES[t.name] || t.name.replace(/_/g, ' ')}:{' '}
                    {t.amount_eur ? `ca. ${money(t.amount_eur, 'EUR')} (${money(t.amount, t.currency)})` : money(t.amount, t.currency)}
                  </div>
                ))
              ) : (
                <span className="empty">geen</span>
              )}
            </td>
            <td>
              <button className="btn-primary" disabled={busy} onClick={() => onChoose(r)}>
                Kies
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RoomsInput({ rooms, setRooms }) {
  const update = (index, change) =>
    setRooms(rooms.map((r, i) => (i === index ? { ...r, ...change } : r)));

  const setChildCount = (index, count) => {
    const children = rooms[index].children.slice(0, count);
    while (children.length < count) children.push(8);
    update(index, { children });
  };

  return (
    <div className="rooms-input">
      {rooms.map((room, i) => (
        <div className="filter-bar room-row" key={i}>
          <span className="room-number">Kamer {i + 1}</span>
          <label>
            Volwassenen
            <select value={room.adults} onChange={(e) => update(i, { adults: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label>
            Kinderen
            <select value={room.children.length} onChange={(e) => setChildCount(i, Number(e.target.value))}>
              {[0, 1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {room.children.map((age, c) => (
            <label key={c}>
              Leeftijd kind {c + 1}
              <select
                value={age}
                onChange={(e) =>
                  update(i, {
                    children: room.children.map((a, j) => (j === c ? Number(e.target.value) : a)),
                  })
                }
              >
                {Array.from({ length: 18 }, (_, n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {rooms.length > 1 && (
            <button
              type="button"
              className="link-button"
              onClick={() => setRooms(rooms.filter((_, j) => j !== i))}
            >
              kamer weghalen
            </button>
          )}
        </div>
      ))}
      {rooms.length < MAX_ROOMS && (
        <button
          type="button"
          className="link-button"
          onClick={() => setRooms([...rooms, { adults: 2, children: [] }])}
        >
          kamer toevoegen
        </button>
      )}
    </div>
  );
}

function GuestsForm({ booking, onBook, busy }) {
  const [guests, setGuests] = useState(() =>
    booking.rooms.map((room) => [
      ...Array.from({ length: room.adults }, () => ({ first_name: '', last_name: '', age: null })),
      ...room.children.map((age) => ({ first_name: '', last_name: '', age })),
    ])
  );

  const update = (r, g, field, value) =>
    setGuests(
      guests.map((room, i) =>
        i === r ? room.map((guest, j) => (j === g ? { ...guest, [field]: value } : guest)) : room
      )
    );

  const submit = (e) => {
    e.preventDefault();
    onBook(
      guests.map((room) =>
        room.map((g) => ({
          first_name: g.first_name.trim(),
          last_name: g.last_name.trim(),
          ...(g.age === null ? {} : { age: g.age }),
        }))
      )
    );
  };

  return (
    <form onSubmit={submit} className="booking-form">
      <p className="intro">
        De namen zoals in het paspoort, accenten mogen: RateHawk bewaart ze ongewijzigd. De eerste
        volwassene van elke kamer is de naam waarop het hotel de kamer zet.
      </p>
      {guests.map((room, r) => (
        <div key={r} className="guest-room">
          <p className="booking-label">Kamer {r + 1}</p>
          {room.map((g, i) => (
            <div className="filter-bar" key={i}>
              <span className="room-number">
                {g.age === null ? `Volwassene ${i + 1}` : `Kind, ${g.age} jaar`}
              </span>
              <label>
                Voornaam
                <input
                  type="text"
                  value={g.first_name}
                  onChange={(e) => update(r, i, 'first_name', e.target.value)}
                  required
                />
              </label>
              <label>
                Achternaam
                <input
                  type="text"
                  value={g.last_name}
                  onChange={(e) => update(r, i, 'last_name', e.target.value)}
                  required
                />
              </label>
            </div>
          ))}
        </div>
      ))}
      <button type="submit" disabled={busy}>
        {busy ? 'Bezig met boeken' : `Boeken voor ${eur(booking.price_eur, booking.price, booking.currency)}`}
      </button>
    </form>
  );
}

function BookingCard({ booking, hotel, onCancel, cancelling, onRefresh, onNew }) {
  const [asking, setAsking] = useState(false);
  // Right after a confirmation RateHawk's order system may not know the order
  // yet, and cancelling then gets order_not_found. So the cancel button waits
  // until /order says the order is there; usually seconds, at most a minute.
  const [orderKnown, setOrderKnown] = useState(false);
  const [orderAsked, setOrderAsked] = useState(0);
  useEffect(() => {
    setOrderKnown(false);
    setOrderAsked(0);
  }, [booking.id]);
  useEffect(() => {
    if (booking.status !== 'succeeded' || orderKnown) return undefined;
    const timer = setTimeout(
      () => {
        hotelBookingApi
          .order(booking.id)
          .then((r) => setOrderKnown(r.data.known))
          .catch(() => {})
          .finally(() => setOrderAsked((n) => n + 1));
      },
      orderAsked === 0 ? 0 : POLL_MS
    );
    return () => clearTimeout(timer);
  }, [booking.id, booking.status, orderKnown, orderAsked]);
  // The refresh link used to give no sign of life: it re-read our own row,
  // which usually had not changed, so the screen looked the same (reported
  // 06/10/2026). Now it says it is busy, and when it last checked.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  useEffect(() => {
    setRefreshedAt(null);
    setRefreshFailed(false);
  }, [booking.id]);
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshFailed(false);
    // Ask about the order again right away, not at the next poll.
    if (booking.status === 'succeeded' && !orderKnown) setOrderAsked(0);
    onRefresh()
      .then(() => setRefreshedAt(new Date()))
      .catch(() => setRefreshFailed(true))
      .finally(() => setRefreshing(false));
  };
  const [, setTick] = useState(0);
  // Redraw every second while pending, so the waiting time keeps counting
  // even when one status request takes long.
  useEffect(() => {
    if (booking.status !== 'pending') return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [booking.status]);
  const nights = nightsBetween(booking.checkin, booking.checkout);
  const waited =
    booking.status === 'pending' && booking.started_at
      ? Math.round((Date.now() - new Date(booking.started_at)) / 1000)
      : null;

  return (
    <div className="booking-panel">
      <div className="booking-head">
        <h4>
          Boeking {booking.id} <StatusBadge status={booking.status} />
          {booking.environment === 'sandbox' && <span className="badge">sandbox</span>}
        </h4>
        <span className="small-line">
          {refreshFailed && 'vernieuwen mislukt, probeer opnieuw '}
          {!refreshFailed && refreshedAt &&
            `bijgewerkt om ${refreshedAt.toLocaleTimeString('nl-BE', { timeZone: 'Europe/Brussels' })} `}
          <button className="link-button" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'bezig' : 'vernieuwen'}
          </button>
        </span>
      </div>
      <div className="detail-header">
        <span>{booking.accommodation_name}</span>
        <span>
          {date(booking.checkin)} tot {date(booking.checkout)}, {nights} {nights === 1 ? 'nacht' : 'nachten'}
        </span>
        <span>{occupancyText(booking.rooms)}</span>
        <span>
          {booking.room_name}, {MEALS[booking.meal] || booking.meal}
        </span>
        <span>{eur(booking.price_eur, booking.price, booking.currency)}</span>
        {booking.currency !== 'EUR' && booking.price_eur && (
          <span>
            RateHawk rekent af: {money(booking.price, booking.currency)}
          </span>
        )}
        {booking.order_id && <span>RateHawk-order {booking.order_id}</span>}
      </div>

      {booking.status === 'pending' && (
        <div className="message warning">
          In behandeling. RateHawk heeft de boeking nog niet bevestigd; we vragen elke 5 seconden de
          status op, tot bevestigd of mislukt. Dat duurt in de sandbox meestal een minuut.
          {waited !== null && ` Bezig sinds ${waited} s.`}
        </div>
      )}
      {booking.status === 'succeeded' && (
        <div className="message success">
          Bevestigd door RateHawk op {moment(booking.finished_at)}. Bevestigingsnummer {booking.partner_order_id}.
        </div>
      )}
      {booking.status === 'failed' && (
        <div className="message error">Mislukt: {failureText(booking.error_code)}. Er is niets geboekt.</div>
      )}
      {booking.status === 'cancelled' && (
        <div className="message success">
          Geannuleerd op {moment(booking.cancelled_at)}. Annuleringskosten{' '}
          {eur(booking.cancellation_fee_eur, booking.cancellation_fee || 0, booking.currency)}, terugbetaald{' '}
          {eur(booking.refunded_eur, booking.refunded || 0, booking.currency)}.
        </div>
      )}
      {booking.status === 'succeeded' && booking.error_code && (
        <div className="message error">Vorige annulering geweigerd: {booking.error_code}. De boeking staat nog.</div>
      )}

      {booking.status === 'succeeded' &&
        (asking ? (
          <div className="message warning">
            Deze boeking annuleren? De kosten volgen de annuleringsvoorwaarden van het tarief.
            <div className="button-row">
              <button className="btn-delete" disabled={cancelling} onClick={onCancel}>
                {cancelling ? 'Bezig met annuleren' : 'Ja, annuleren'}
              </button>
              <button className="btn-secondary" disabled={cancelling} onClick={() => setAsking(false)}>
                Nee
              </button>
            </div>
          </div>
        ) : (
          <div className="button-row">
            <button className="btn-delete" disabled={!orderKnown} onClick={() => setAsking(true)}>
              Annuleren
            </button>
            {!orderKnown && (
              <span className="small-line">
                Annuleren kan zodra RateHawk de order kent, meestal binnen een minuut na de bevestiging.
              </span>
            )}
          </div>
        ))}

      {['succeeded', 'failed', 'cancelled'].includes(booking.status) && (
        <div className="button-row">
          <button className="btn-secondary" onClick={onNew}>
            Nieuwe boeking
          </button>
        </div>
      )}
      {hotel && booking.status === 'succeeded' && <HotelInfo hotel={hotel} />}
    </div>
  );
}

function BookingList({ bookings, tours, onOpen, activeId }) {
  const stayName = {};
  tours.forEach((t) =>
    t.stays.forEach((s) => {
      stayName[s.id] = `${t.name_nl}, ${s.sequence_number}. ${s.location_name}`;
    })
  );
  if (!bookings.length) return <p className="empty">Nog geen boekingen.</p>;
  return (
    <table className="insights-table">
      <thead>
        <tr>
          <th>Nr</th>
          <th>Aangemaakt</th>
          <th>Hotel</th>
          <th>Reis</th>
          <th>Verblijf</th>
          <th>Bezetting</th>
          <th className="number">Prijs</th>
          <th>Status</th>
          <th>Order</th>
        </tr>
      </thead>
      <tbody>
        {bookings.map((b) => (
          <tr key={b.id} onClick={() => onOpen(b)} className={b.id === activeId ? 'selected' : ''}>
            <td>{b.id}</td>
            <td>{moment(b.created_at)}</td>
            <td>{b.accommodation_name}</td>
            <td>{b.tour_stay_id ? stayName[b.tour_stay_id] || `overnachting ${b.tour_stay_id}` : ''}</td>
            <td>
              {date(b.checkin)}, {nightsBetween(b.checkin, b.checkout)} n.
            </td>
            <td>{occupancyText(b.rooms)}</td>
            <td className="number">{eur(b.price_eur, b.price, b.currency)}</td>
            <td>
              <StatusBadge status={b.status} />
            </td>
            <td>{b.order_id || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HotelBooking() {
  const [hotels, setHotels] = useState([]);
  const [tours, setTours] = useState([]);
  const [bookings, setBookings] = useState([]);

  const [mode, setMode] = useState('tour');
  const [tourId, setTourId] = useState('');
  const [stayId, setStayId] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [checkin, setCheckin] = useState(inDays(60));
  const [nights, setNights] = useState(2);
  const [rooms, setRooms] = useState([{ adults: 2, children: [] }]);
  const [residency, setResidency] = useState('be');

  const [rates, setRates] = useState(null);
  const [booking, setBooking] = useState(null);
  const [priceAccepted, setPriceAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState(null);
  // Set when the booking step gave no answer. The backend may still be busy
  // with it (measured 06/10/2026: the step outlasted the 60 seconds nginx
  // waits, and the booking started anyway), so the screen watches the row
  // instead of offering to book again.
  const [watchingSince, setWatchingSince] = useState(null);

  const loadBookings = useCallback(
    () =>
      hotelBookingApi
        .list()
        .then((r) => setBookings(r.data))
        .catch(() => {}),
    []
  );

  useEffect(() => {
    Promise.all([hotelBookingApi.hotels(), hotelBookingApi.tours()])
      .then(([h, t]) => {
        setHotels(h.data);
        setTours(t.data);
      })
      .catch(() => setMessage({ type: 'error', text: 'De hotels en reizen konden niet opgehaald worden.' }));
    loadBookings();
  }, [loadBookings]);

  const tour = tours.find((t) => String(t.id) === String(tourId));
  const stay = tour && tour.stays.find((s) => String(s.id) === String(stayId));
  const hotel = hotels.find((h) => String(h.id) === String(hotelId));
  const stayHotels = stay ? hotels.filter((h) => stay.hotel_ids.includes(h.id)) : [];
  const minNights = stay ? stay.min_nights || 1 : 1;
  const maxNights = stay ? stay.max_nights || stay.min_nights || 30 : 30;

  // The status step, every five seconds while the booking is pending. The
  // backend decides when the cut-off has passed, so this simply keeps asking.
  useEffect(() => {
    if (!booking || booking.status !== 'pending') return undefined;
    const timer = setTimeout(() => {
      hotelBookingApi
        .status(booking.id)
        .then((r) => {
          setBooking(r.data);
          if (r.data.status !== 'pending') loadBookings();
        })
        .catch(() => setBooking({ ...booking }));
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [booking, loadBookings]);

  // While watching, ask for the row every five seconds until it leaves
  // prebooked. After three minutes without a start, nothing was booked.
  useEffect(() => {
    if (!watchingSince || !booking || booking.status !== 'prebooked') return undefined;
    const timer = setTimeout(() => {
      hotelBookingApi
        .get(booking.id)
        .then((r) => {
          if (r.data.status !== 'prebooked') {
            setWatchingSince(null);
            setMessage(null);
            setBooking(r.data);
            loadBookings();
          } else if (Date.now() - watchingSince > 180000) {
            setWatchingSince(null);
            setMessage({
              type: 'error',
              text: 'De boeking is na drie minuten nog niet gestart. Er is niets geboekt; je kan opnieuw boeken.',
            });
          } else {
            setBooking(r.data);
          }
        })
        .catch(() => setBooking({ ...booking }));
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [watchingSince, booking, loadBookings]);

  const resetChoice = () => {
    setWatchingSince(null);
    setRates(null);
    setBooking(null);
    setPriceAccepted(false);
    setMessage(null);
  };

  const chooseTour = (id) => {
    setTourId(id);
    const t = tours.find((x) => String(x.id) === String(id));
    const first = t && t.stays.find((s) => s.hotel_ids.length);
    chooseStay(first ? String(first.id) : '', t);
  };

  const chooseStay = (id, inTour = tour) => {
    setStayId(id);
    const s = inTour && inTour.stays.find((x) => String(x.id) === String(id));
    setHotelId(s && s.hotel_ids.length ? String(s.hotel_ids[0]) : '');
    if (s && s.min_nights) setNights(s.min_nights);
    resetChoice();
  };

  const query = () => ({
    accommodation_id: Number(hotelId),
    tour_stay_id: mode === 'tour' && stayId ? Number(stayId) : null,
    checkin,
    checkout: addDays(checkin, Number(nights)),
    rooms,
    residency,
  });

  const fetchRates = async (e) => {
    e.preventDefault();
    resetChoice();
    setBusy(true);
    try {
      const r = await hotelBookingApi.rates(query());
      setRates(r.data);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'Tarieven opvragen is niet gelukt.') });
    } finally {
      setBusy(false);
    }
  };

  const chooseRate = async (rate) => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await hotelBookingApi.prebook({
        ...query(),
        book_hash: rate.book_hash,
        room: rate.room,
        meal: rate.meal,
        price: rate.price,
        currency: rate.currency,
      });
      setBooking(r.data);
      setPriceAccepted(false);
      loadBookings();
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error, 'De prijscontrole is niet gelukt.') });
    } finally {
      setBusy(false);
    }
  };

  const abortPriceChange = () => {
    setBooking(null);
    setMessage({ type: 'warning', text: 'Afgebroken, er is niets geboekt. Kies een ander tarief of vraag opnieuw op.' });
  };

  const book = async (guests) => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await hotelBookingApi.book(booking.id, {
        guests,
        price_confirmed: booking.price_changed && priceAccepted,
      });
      setBooking(r.data);
      loadBookings();
    } catch (error) {
      const detail = error.response && error.response.data && error.response.data.detail;
      if (error.response && error.response.status < 500 && detail) {
        // A refusal from our backend: nothing was sent to RateHawk.
        setMessage({ type: 'error', text: errorText(error, 'Boeken is niet gelukt.') });
        refresh().catch(() => {});
      } else {
        setMessage({
          type: 'warning',
          text:
            'Het antwoord van de boekingsstap bleef uit. De boeking kan intussen wel gestart zijn; ' +
            'we kijken het elke 5 seconden na. Boek niet opnieuw.',
        });
        setWatchingSince(Date.now());
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    setMessage(null);
    try {
      const r = await hotelBookingApi.cancel(booking.id);
      setBooking(r.data);
    } catch (error) {
      const answered = error.response && error.response.status === 409;
      setMessage({
        type: 'error',
        text: answered
          ? errorText(error, 'Annuleren is geweigerd.')
          : 'Geen antwoord binnen de wachttijd. De annulering kan nog lopen; vernieuw de boeking over een minuut.',
      });
      refresh().catch(() => {});
    } finally {
      setCancelling(false);
      loadBookings();
    }
  };

  // Our row again, and the list with it. A pending booking goes through the
  // status step, so a refresh also asks RateHawk instead of only our table.
  const refresh = () => {
    if (!booking) return Promise.resolve();
    const request =
      booking.status === 'pending' ? hotelBookingApi.status(booking.id) : hotelBookingApi.get(booking.id);
    return request.then((r) => {
      setBooking(r.data);
      loadBookings();
    });
  };

  const openBooking = (b) => {
    setWatchingSince(null);
    setMessage(null);
    setRates(null);
    setPriceAccepted(false);
    setBooking(b);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prebooked = booking && booking.status === 'prebooked';
  const mustConfirm = prebooked && booking.needs_confirmation && !priceAccepted;
  const bookingHotel = booking && hotels.find((h) => h.id === booking.accommodation_id);
  const difference =
    booking && Number(booking.previous_price)
      ? ((Number(booking.price) - Number(booking.previous_price)) / Number(booking.previous_price)) * 100
      : 0;

  return (
    <div>
      <h2>Hotel boeken</h2>
      <p className="intro">
        Boeken bij RateHawk vanuit een reis en overnachting, of rechtstreeks bij een gekoppeld hotel. Voorlopig
        alleen in de sandbox: er wordt niets echt geboekt of betaald.
      </p>

      {message && <div className={`message ${message.type}`}>{message.text}</div>}

      {!booking && (
        <form onSubmit={fetchRates} className="booking-form">
          <div className="filter-bar">
            <label className="checkbox-label">
              <input
                type="radio"
                name="mode"
                checked={mode === 'tour'}
                onChange={() => {
                  setMode('tour');
                  resetChoice();
                }}
              />
              Vanuit een reis
            </label>
            <label className="checkbox-label">
              <input
                type="radio"
                name="mode"
                checked={mode === 'hotel'}
                onChange={() => {
                  setMode('hotel');
                  setStayId('');
                  resetChoice();
                }}
              />
              Rechtstreeks een hotel
            </label>
          </div>

          <div className="filter-bar">
            {mode === 'tour' ? (
              <>
                <label>
                  Reis
                  <select value={tourId} onChange={(e) => chooseTour(e.target.value)} required>
                    <option value="">kies een reis</option>
                    {tours.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name_nl}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Overnachting
                  <select value={stayId} onChange={(e) => chooseStay(e.target.value)} required disabled={!tour}>
                    <option value="">kies een overnachting</option>
                    {tour &&
                      tour.stays.map((s) => (
                        <option key={s.id} value={s.id} disabled={!s.hotel_ids.length}>
                          {s.sequence_number}. {s.location_name} ({s.outward_return === 'return' ? 'terug' : 'heen'},{' '}
                          {s.min_nights === s.max_nights ? s.min_nights : `${s.min_nights || '?'} tot ${s.max_nights || '?'}`}{' '}
                          nachten){s.hotel_ids.length ? '' : ', geen gekoppeld hotel'}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Hotel
                  <select
                    value={hotelId}
                    onChange={(e) => {
                      setHotelId(e.target.value);
                      resetChoice();
                    }}
                    required
                    disabled={!stay}
                  >
                    <option value="">kies een hotel</option>
                    {stayHotels.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                        {h.category ? ` (${h.category})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label>
                Hotel
                <select
                  value={hotelId}
                  onChange={(e) => {
                    setHotelId(e.target.value);
                    resetChoice();
                  }}
                  required
                >
                  <option value="">kies een hotel</option>
                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                      {h.location_name ? `, ${h.location_name}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="filter-bar">
            <label>
              Aankomst
              <input
                type="date"
                value={checkin}
                min={inDays(1)}
                onChange={(e) => {
                  setCheckin(e.target.value);
                  resetChoice();
                }}
                required
              />
            </label>
            <label>
              Nachten
              <select
                value={nights}
                onChange={(e) => {
                  setNights(Number(e.target.value));
                  resetChoice();
                }}
              >
                {Array.from({ length: maxNights - minNights + 1 }, (_, i) => minNights + i).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <span className="count">{checkin && addDays(checkin, Number(nights)) ? `vertrek ${date(addDays(checkin, Number(nights)))}` : ''}</span>
            <label>
              Nationaliteit van de gasten
              <select
                value={residency}
                onChange={(e) => {
                  setResidency(e.target.value);
                  resetChoice();
                }}
              >
                {RESIDENCIES.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <RoomsInput
            rooms={rooms}
            setRooms={(r) => {
              setRooms(r);
              resetChoice();
            }}
          />

          <button type="submit" disabled={busy || !hotelId}>
            {busy && !rates ? 'Tarieven worden opgevraagd' : 'Tarieven opvragen'}
          </button>
        </form>
      )}

      {!booking && rates && (
        <>
          <h3>
            Tarieven {hotel ? `in ${hotel.name}` : ''}, {nights} {Number(nights) === 1 ? 'nacht' : 'nachten'},{' '}
            {occupancyText(rooms)}
          </h3>
          <p className="intro">
            Live opgevraagd. Bij het kiezen controleert RateHawk de prijs opnieuw. Annuleringstermijnen in
            Belgische tijd.
          </p>
          {rooms.length > 1 && (
            <p className="intro">
              Elk tarief geldt voor {rooms.length === 2 ? 'beide kamers' : `alle ${rooms.length} kamers`} samen: je kiest één tarief, en elke kamer krijgt
              die kamersoort en maaltijd, met de bezetting zoals hierboven. Verschillende kamersoorten per kamer
              kan RateHawk niet in één boeking; dat zijn aparte boekingen.
            </p>
          )}
          <RatesTable rates={rates} roomCount={rooms.length} onChoose={chooseRate} busy={busy} />
          <HotelInfo hotel={hotel} />
        </>
      )}

      {mustConfirm && (
        <div className="booking-panel price-change">
          <h4>De prijs is gewijzigd</h4>
          <p>
            Getoond was {eur(booking.previous_price_eur, booking.previous_price, booking.currency)}. RateHawk
            vraagt nu <strong>{eur(booking.price_eur, booking.price, booking.currency)}</strong>
            {difference ? ` (${difference > 0 ? '+' : ''}${difference.toFixed(1).replace('.', ',')}%)` : ''}.
            Leg dit voor aan de klant. Pas na bevestiging kan je boeken.
          </p>
          <div className="button-row">
            <button className="btn-primary" onClick={() => setPriceAccepted(true)}>
              Nieuwe prijs bevestigen
            </button>
            <button className="btn-secondary" onClick={abortPriceChange}>
              Afbreken
            </button>
          </div>
        </div>
      )}

      {prebooked && !mustConfirm && (
        <>
          <h3>Gasten</h3>
          <div className="detail-header">
            <span>{booking.accommodation_name}</span>
            <span>
              {date(booking.checkin)} tot {date(booking.checkout)}
            </span>
            <span>
              {booking.room_name}, {MEALS[booking.meal] || booking.meal}
            </span>
            <span>{occupancyText(booking.rooms)}</span>
            <span>
              {eur(booking.price_eur, booking.price, booking.currency)}
              {booking.price_changed && ' (nieuwe prijs, bevestigd)'}
            </span>
          </div>
          {!watchingSince && (
            <>
              <HotelInfo hotel={bookingHotel} />
              <GuestsForm key={booking.id} booking={booking} onBook={book} busy={busy} />
              <div className="button-row">
                <button className="btn-secondary" onClick={() => setBooking(null)}>
                  Terug naar de keuze
                </button>
              </div>
            </>
          )}
        </>
      )}

      {booking && !prebooked && (
        <BookingCard
          booking={booking}
          hotel={bookingHotel}
          onCancel={cancel}
          cancelling={cancelling}
          onRefresh={refresh}
          onNew={() => {
            setBooking(null);
            setRates(null);
            setMessage(null);
          }}
        />
      )}

      <h3>Onze boekingen</h3>
      <BookingList bookings={bookings} tours={tours} onOpen={openBooking} activeId={booking && booking.id} />
    </div>
  );
}

export default HotelBooking;
